// «Назад» на витрине, открытой без истории, и системный «назад» над модалкой.
//
// 1. `navigate(-1)` на странице, открытой по прямой ссылке (из мессенджера, по
//    ссылке магазина /s/<код>, после перезагрузки на новой вкладке), уводил с
//    сайта или не делал ничего: в истории вкладки до неё нет страниц OSMIX.
//    React Router кладёт в `history.state` порядковый номер записи `idx`; 0 -
//    первая страница сайта в этой вкладке (у /s/<код> тоже 0: витрина заменяет
//    её через `replace`). Тогда «Назад» ведёт на запасной адрес.
// 2. Модалка на телефоне выглядит как отдельный экран, и системный «назад»
//    ждут, что он её закроет. Без этого он уводил со страницы прямо из-под
//    открытой модалки. Пока модалка открыта, в истории лежит своя запись того
//    же адреса; «назад» снимает её и закрывает модалку, страница остаётся.
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router";

/** Есть ли в этой вкладке страница сайта, на которую можно вернуться. */
export function hasInAppHistory(state: unknown = typeof window === "undefined" ? null : window.history.state): boolean {
	if (!state || typeof state !== "object") return false;
	const idx = (state as { idx?: unknown }).idx;
	return typeof idx === "number" && idx > 0;
}

/** «Назад» с запасным адресом: истории нет - на `fallback` вместо ухода с сайта. */
export function useGoBack(fallback = "/") {
	const navigate = useNavigate();
	return useCallback(() => {
		if (hasInAppHistory()) navigate(-1);
		else navigate(fallback, { replace: true });
	}, [navigate, fallback]);
}

const OVERLAY_KEY = "osmixOverlay";

// Свои `history.back()` (модалку закрыли крестиком - снимаем её запись) не
// должны закрывать следующую модалку: `back()` асинхронный, и его `popstate`
// может прийти, когда уже открыта другая. Общий слушатель помечает такой
// `popstate` как свой; он подписан раньше слушателей модалок, поэтому они
// видят пометку в том же событии.
let selfBacks = 0;
let popIsSelf = false;
const waitingPush = new Set<() => void>();
let installed = false;

function installPopListener() {
	if (installed) return;
	installed = true;
	window.addEventListener("popstate", () => {
		popIsSelf = selfBacks > 0;
		if (popIsSelf) selfBacks -= 1;
		if (selfBacks === 0 && waitingPush.size > 0) {
			const pending = [...waitingPush];
			waitingPush.clear();
			for (const push of pending) push();
		}
	});
}

/**
 * Пока `open`, системный «назад» закрывает модалку (`onClose`), а не страницу.
 * Закрыли иначе (крестик, фон, Esc) - своя запись снимается из истории, чтобы
 * следующий «назад» не тратился впустую.
 */
export function useCloseOnBack(open: boolean, onClose: () => void) {
	const closeRef = useRef(onClose);
	useEffect(() => {
		closeRef.current = onClose;
	});

	useEffect(() => {
		if (!open || typeof window === "undefined") return;
		installPopListener();
		const marker = Math.random().toString(36).slice(2);
		let pushed = false;
		let done = false;

		const push = () => {
			if (done) return;
			const state = (window.history.state ?? {}) as Record<string, unknown>;
			window.history.pushState({ ...state, [OVERLAY_KEY]: marker }, "");
			pushed = true;
		};
		const onPop = () => {
			if (popIsSelf || !pushed || done) return;
			done = true;
			closeRef.current();
		};

		window.addEventListener("popstate", onPop);
		if (selfBacks === 0) push();
		else waitingPush.add(push);

		return () => {
			window.removeEventListener("popstate", onPop);
			waitingPush.delete(push);
			const state = window.history.state as Record<string, unknown> | null;
			const ownEntryOnTop = pushed && !done && state?.[OVERLAY_KEY] === marker;
			done = true;
			if (ownEntryOnTop) {
				selfBacks += 1;
				window.history.back();
			}
		};
	}, [open]);
}
